import React, { useCallback, useEffect, useState } from "react";
import {
  Table,
  Tooltip,
  Tag,
  Space,
  Button,
  Modal,
  notification,
  Popconfirm,
  Radio,
  Form,
  Select,
  DatePicker,
  Card,
  Row,
  Col,
} from "antd";
import { useTranslation } from "react-i18next";
import {
  InfoCircleOutlined,
  DeleteOutlined,
  SaveOutlined,
  FilePdfOutlined,
  PlusOutlined,
  EditOutlined,
  SendOutlined
} from "@ant-design/icons";
import AddForm from "../components/Buildings/BuildingUnitSetupChanges/AddForm";
import { coreApi } from "../api/calls";
import EmployeeLink from "../components/Employees/EmployeeLink";
import BuildingUnitLink from "../components/Buildings/BuildingUnitLink";
import fileDownload from "js-file-download";
import PartnerLink from "../components/Partners/PartnerLink";
import PartnerSelect from "../components/Partners/PartnerSelect";
import RruleDaySelect from "../components/Scheduler/RruleDaySelect";
import { rrulestr } from "rrule";
import { getAreaScope } from "../actions";
import { useSelector } from "react-redux";
import { sortPartners } from "../components/helpers/sort";
import MassEditForm from "../components/Buildings/BuildingUnitSetupChanges/MassEditForm";
import EditManyForm from "../components/Buildings/BuildingUnitSetupChanges/EditManyForm";
import { fetchManagers } from "../api/managerApi";
import TextArea from "antd/lib/input/TextArea";
import ScheduleMassTerminate from "../components/Scheduler/MultiStepEdit/ScheduleMultiStepMassEdit";
import { SlideDown } from "react-slidedown";
import MassInvoiceDuringMonthForm from "../components/Partners/Invoice/MassInvoiceDuringMonthForm";
import MassBuildingUnitCleaningManagement from "../components/Buildings/BuildingUnitSetupChanges/MassBuildingUnitCleaningManagement";
import {getTheNameOfFrequency} from "../components/helpers/rrule";
import PartnerInformSelect from "../components/Scheduler/PartnerInformSelect";
import ClientInformSelect from "../components/Scheduler/ClientInformSelect";
import PdfSettingsForm from "../components/PdfSettingsForm";
import SendCleaningSchedulesForPartnerModal from "../components/Scheduler/SendCleaningSchedulesForPartnerModal";
import {showActiveDays} from "../components/helpers/schedule";

const getDatedPdfFileName = (name) => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}_${name}.pdf`;
};

const BuildingUnitSetupChangesIndex = () => {
  const { t } = useTranslation();
  const user = JSON.parse(localStorage.getItem("user"));

  const areaScope = useSelector(getAreaScope);
  const selectedCity = areaScope.areaScope;

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 100,
    total: 0,
  });

  const [isDownloadModalVisible, setIsDownloadModalVisible] = useState(false);
  const [isDownloadCleaningPartnerPdfModalVisible, setIsDownloadCleaningPartnerPdfModalVisible] = useState(false);
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditManyModalVisible, setIsEditManyModalVisible] = useState(false);
  const [isMassEditModalVisible, setIsMassEditModalVisible] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [sorter, setSorter] = useState({});
  const [managers, setManagers] = useState([]);
  const [actions, setActions] = useState([]);
  const [filters, setFilters] = useState({
    status_id: [...[1, 3]],
    date_from: null,
    date_to: null,
  });

  const [productType, setProductType] = useState("automatic");
  const [displayPrice, setDisplayPrice] = useState("total");
  const [selectedLanguage, setSelectedLanguage] = useState("automatic");
  const [hideStreetNumber, setHideStreetNumber] = useState(false);

  const [defaultPartners, setDefaultPartners] = useState([]);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [isMassCleaningManagementModalVisible, setIsMassCleaningManagementModalVisible] = useState(false);
  const [isSendSchedulesForPartnerModalVisible, setIsSendSchedulesForPartnerModalVisible] = useState(false);

  const showEditModal = (record, field) => {
    setEditingRecord(record);
    setEditingField(field);
    setIsEditModalVisible(true);
  };

  const handleSave = async () => {
    const updatedRecord = { ...editingRecord };

    try {
      await coreApi.put(`/building-unit-setup-changes/${updatedRecord.id}`, {
        [editingField]: updatedRecord[editingField],
      });

      notification.success({ message: t("common.saveSuccess") });
      setIsEditModalVisible(false);
      fetchBuildingUnitSetupChanges(pagination, sorter, filters);
    } catch (error) {
      notification.error({ message: t("common.saveFailed") });
    }
  };

  const [formKeys, setFormKeys] = useState({
    addForm: Math.random(),
    massEditForm: Math.random(),
    editManyForm: Math.random(),
    downloadForm: Math.random(),
    downloadCleaningPartnerForm: Math.random(),
  });

  const fetchBuildingUnitSetupChanges = (paginationParams = {}, sorterParams = {}, filtersParams = {}) => {
    const controller = new AbortController();
    const { signal } = controller;
    setLoading(true);

    const { current, pageSize } = paginationParams;
    const { order, field } = sorterParams;

    const filterParams = Object.keys(filtersParams).reduce((acc, key) => {
      const value = filtersParams[key];
      acc[key] = Array.isArray(value) ? value.join(",") : value || null;
      return acc;
    }, {});

    filterParams.city_branch_id = selectedCity || null;
    const sort_by = (field === "action_week_from" || !field)
      ? ((order === "descend" || order === "desc") ? "action_week_to" : "action_week_from")
      : field;

    coreApi
      .get("/building-unit-setup-changes", {
        params: {
          page: current,
          per_page: pageSize,
          order: order === "descend" ? "desc" : "asc",
          sort_by: sort_by,
          ...filterParams,
        },
      })
      .then((response) => {
        if (!signal.aborted) {
          setSelectedRowKeys([]);
          const rowData = calculateRowSpan(response.data.data);
          setData(rowData);
          setPagination({
            current: response.data.current_page,
            pageSize: response.data.per_page,
            total: response.data.total,
          });
        }
      })
      .catch((error) => {
        notification.error({ message: t("buildingSetupChanges.messages.failedLoadData") + (error.response?.data?.message || "") });
      })
      .finally(() => setLoading(false));

    return () => controller.abort();
  };

  const fetchSetupChangesStatuses = useCallback(() => {
    coreApi.get("/building-unit-setup-changes/actions/statuses")
      .then((response) => setStatuses(response.data))
      .catch((error) => {
        notification.error({ message: t("buildingSetupChanges.messages.failedLoadData") + (error.response?.data?.message || "") });
      });
  }, [t]);

  const getManagers = useCallback(() => {
    fetchManagers()
      .then(setManagers)
      .catch(() => {
        notification.error({ message: t("buildingSetupChanges.messages.failedLoadData") });
      });
  }, [t]);

  const fetchPartners = useCallback(() => {
    coreApi.get("/partners/all")
      .then((res) => setDefaultPartners(sortPartners(res.data)))
      .catch(() => {
        notification.error({ message: t("common.loadFailed") });
      });
  }, [t]);

  const fetchActions = useCallback(() => {
    coreApi.get("/building-unit-setup-changes/actions/list-actions")
      .then((res) => setActions(res.data))
      .catch((error) => {
        notification.error({ message: t("buildingSetupChanges.messages.failedLoadData") + (error.response?.data?.message || "") });
      });
  }, [t]);

  const calculateRowSpan = (data) => {
    const groupingCounts = data.reduce((acc, item) => {
      const key = item.grouping_param;
      if (!acc[key]) {
        acc[key] = { count: 0, index: Object.keys(acc).length + 1 };
      }
      acc[key].count++;
      return acc;
    }, {});

    return data.map((item, index, arr) => {
      const isRowSpanStart = index === 0 || item.grouping_param !== arr[index - 1].grouping_param;
      return {
        ...item,
        rowSpan: isRowSpanStart ? groupingCounts[item.grouping_param].count : 0,
        rowSpanIndex: isRowSpanStart ? groupingCounts[item.grouping_param].index : null,
        rowClass: isRowSpanStart ? "grouped-row" : "",
      };
    });
  };

  const handleStatusChange = (value) => {
    coreApi
      .put("/building-unit-setup-changes/actions/update-statuses", {
        ids: selectedRowKeys,
        status: value,
      })
      .then((response) => {
        notification.success({ message: t("buildingSetupChanges.messages.statusUpdated") });
        setSelectedRowKeys([]);
        fetchBuildingUnitSetupChanges(pagination, sorter, filters);
      })
      .catch((error) => {
        notification.error({ message: t("buildingSetupChanges.messages.failedUpdateStatus") + (error.response?.data?.message || "") });
      });
  };

  const handleDeleteRow = (id) => {
    coreApi
      .delete(`/building-unit-setup-changes/${id}`)
      .then((response) => {
        notification.success({ message: t("common.recordDeleted") });
        fetchBuildingUnitSetupChanges(pagination, sorter, filters);
      })
      .catch((error) => {
        notification.error({ message: t("common.deletionFailed") + (error.response?.data?.message || "") });
      });
  };

  const handleTableChange = (pagination, tableFilters, sorter) => {
    const updatedFilters = { ...filters };

    Object.keys(tableFilters).forEach((key) => {
      if (tableFilters[key]?.length > 0) {
        updatedFilters[key] = tableFilters[key];
      } else {
        updatedFilters[key] = null;
      }
    });

    let sorterParams = {};

    if (sorter.order) {
      sorterParams = {
        order: sorter.order,
        field: sorter.field || sorter.columnKey || "id",
      };
    } else {
      sorterParams = {
        order: null,
        field: null,
      };
    }

    setFilters(updatedFilters);
    setPagination(pagination);
    setSorter(sorterParams);

    fetchBuildingUnitSetupChanges(pagination, sorterParams, updatedFilters);
  };

  const handleDownloadPdf = () => {
    if (!productType || displayPrice === null) {
      notification.error({ message: t("buildingSetupChanges.messages.selectOptions") });
      return;
    }

    coreApi
      .post(
        "/building-unit-setup-changes/actions/download/pdf",
        {
          ids: selectedRowKeys,
          productType,
          displayPrice,
          language: selectedLanguage,
        },
        { responseType: "blob" },
      )
      .then((response) => {
        fileDownload(response.data, getDatedPdfFileName("zakazky_nabidka"));
        setIsDownloadModalVisible(false);
      })
      .catch((error) => {
        notification.error({ message: t("buildingSetupChanges.messages.failedDownloadPdf") + (error.response?.data?.message || "") });
      });
  };

  const handleDownloadCleaningPartnerPdfPdf = () => {
    if (!productType || displayPrice === null) {
      notification.error({ message: t("buildingSetupChanges.messages.selectOptions") });
      return;
    }

    coreApi
      .post(
        "/building-unit-setup-changes/actions/download/cleaning-partner-pdf",
        {
          ids: selectedRowKeys,
          productType,
          displayPrice,
          language: selectedLanguage,
          hideStreetNumber: hideStreetNumber,
        },
        { responseType: "blob" },
      )
      .then((response) => {
        fileDownload(response.data, getDatedPdfFileName("zakazky_detail"));
        setIsDownloadCleaningPartnerPdfModalVisible(false);
      })
      .catch((error) => {
        notification.error({ message: t("buildingSetupChanges.messages.failedDownloadPdf") + (error.response?.data?.message || "") });
      });
  };

  const getTheActiveDays = (record, t) => {
    let days = [];
    if (
      record.building_unit &&
      Array.isArray(record.building_unit.cleaning_days) &&
      record.building_unit.cleaning_days.length
    ) {
      record.building_unit.cleaning_days.forEach(day => {
        day = day.includes(',') ? day.split(',') : [day];
        day.forEach(d => {
          if (!days.includes(d)) {
            days.push(d);
          }
        });
      });
    }

    /*if (
      record.building_unit &&
      Array.isArray(record.building_unit.service_rrules) &&
      record.building_unit.service_rrules.length
    ) {
        record.building_unit.service_rrules?.forEach((rule) => {
        if (record && record.partner && rule && record.partner?.id === rule?.partner_id) {
          getDays(rule.rrule).forEach(day => {
            if (!days.includes(day)) {
              days.push(day);
            }
          });
        }
      });
    }

    if (record.rrule) {
      const parsedDays = getDays(record.rrule);
      if (parsedDays) {
        parsedDays.forEach(day => {
          if (!days.includes(day)) {
            days.push(day);
          }
        });
      }
    }*/

    return days.length
      ? days.map(day => t("rrules.byweekday." + day)).join(', ')
      : null;
  };

  const formatRruleDays = (rrule) => {
    if (!rrule) {
      return "";
    }

    try {
      const daysMap = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];
      const daysNames = {
        MO: t("rrules.byweekday.MO"),
        TU: t("rrules.byweekday.TU"),
        WE: t("rrules.byweekday.WE"),
        TH: t("rrules.byweekday.TH"),
        FR: t("rrules.byweekday.FR"),
        SA: t("rrules.byweekday.SA"),
        SU: t("rrules.byweekday.SU"),
      };
      const rruleObject = rrulestr(rrule);

      if (!rruleObject.origOptions.byweekday) {
        return "";
      }

      const selectedDays = rruleObject.origOptions.byweekday.map((weekday) => daysMap[weekday.weekday]);
      return selectedDays.map(day => daysNames[day]).join(", ");
    } catch (error) {
      console.error("Invalid rrule format", error);
      return "";
    }
  };

  const handleRruleChange = (rruleString, id, fieldName) => {
    coreApi
      .put(`/building-unit-setup-changes/actions/${id}/update-rrule`, { [fieldName]: rruleString })
      .then((response) => {
        notification.success({ message: t("common.saveSuccess") });
      })
      .catch((error) => {
        notification.error({ message: t("common.saveFailed") + (error.response?.data?.message || "") });
      })
      .finally(() => fetchBuildingUnitSetupChanges(pagination, sorter, filters));
  };

  const daysCount = (rrule) => {
    return formatRruleDays(rrule, t).split(',').length
  };

  useEffect(() => {
    fetchSetupChangesStatuses();
    getManagers();
    fetchActions();
    fetchPartners();
  }, [fetchSetupChangesStatuses, getManagers, fetchActions, fetchPartners]);


  useEffect(() => {
    fetchBuildingUnitSetupChanges(pagination, sorter, filters);
  }, [selectedCity]);

  const renderRruleDaySelect = (rrule, record, fieldName = "substitute_rrule") => (
    <Form key={record.id}>
      <RruleDaySelect
        key={record.id}
        size="small"
        className="mb-0"
        rrule={rrule}
        postBlur={(rruleString) => handleRruleChange(rruleString, record.id, fieldName)}
        onChange={(rruleString) => handleRruleChange(rruleString, record.id, fieldName)}
        showLabel={false}
      />
    </Form>
  );

  const columns = [
    {
      title: t("schedules.status"),
      key: "status_id",
      dataIndex: "status_id",
      sorter: true,
      width: 108,
      fixed: "left",
      filteredValue: filters.status_id?.length ? filters.status_id : null,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8, width: 250 }}>
          <Space direction="vertical">
            <Select
              mode="multiple"
              allowClear
              style={{ width: 220 }}
              placeholder={t("buildingSetupChanges.selectStatus")}
              value={selectedKeys}
              onChange={(value) => {
                const newFilters = {
                  ...filters,
                  status_id: value.length > 0 ? value : null,
                };
                setFilters(newFilters);
                setSelectedKeys(value);
                fetchBuildingUnitSetupChanges(pagination, sorter, newFilters);
                confirm();
              }}
            >
              {statuses.map((status) => (
                <Select.Option key={status.id} value={status.id}>
                  {status.name}
                </Select.Option>
              ))}
            </Select>
          </Space>
        </div>
      ),
      render: (text, record) => {
        if (record.status_id === 1) {
          return <Tag color="red">{t("buildingSetupChanges.statusInSolution")}</Tag>;
        } else if (record.status_id === 3) {
          return <Tag color="orange">{t("buildingSetupChanges.statusInSolutionPackageConfirmed")}</Tag>;
        } else {
          return <Tag color="green">{t("buildingSetupChanges.statusSolved")}</Tag>;
        }
      },
    },
    {
      title: t("common.address"),
      sorter: true,
      key: "address",
      width: 220,
      fixed: "left",
      render: (text, record) => <BuildingUnitLink buildingUnit={record.building_unit} />,
    },
    {
      title: t("common.city"),
      key: "city",
      width: 150,
      fixed: "left",
      sorter: (a, b) => a.city_branch.name.localeCompare(b.city_branch.name),
      render: (text, record) => record.city_branch.name,
    },
    {
      title: t("buildingSetupChanges.action"),
      key: "action_id",
      dataIndex: "action_tag_name",
      sorter: true,
      width: 195,
      fixed: "left",
      filters: actions.map(action => ({
        text: action.translations.find(translation => translation.language_ietf === "cs").tag_name,
        value: action.id,
      })),
      filteredValue: filters.action_id || null,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8, width: 250 }}>
          <Space direction="vertical">
            <Select
              mode="multiple"
              allowClear
              style={{ width: 220 }}
              placeholder={t("buildingSetupChanges.selectAction")}
              value={selectedKeys}
              onChange={(value) => {
                setSelectedKeys(value);
                const newFilters = {
                  ...filters,
                  action_id: value.length > 0 ? value : null,
                };
                setFilters(newFilters);
                fetchBuildingUnitSetupChanges(pagination, sorter, newFilters);
                confirm();
              }}
            >
              {actions.map((action) => (
                <Select.Option key={action.id} value={action.id}>
                  {action.translations.find(translation => translation.language_ietf === "cs").tag_name}
                </Select.Option>
              ))}
            </Select>
          </Space>
        </div>
      ),
      render: (text, record) => {
        const actionTranslation = record.action?.translations.find((translation) => translation.language_ietf === "cs");
        const displayText = actionTranslation ? actionTranslation.tag_name : text;

        const actionColors = {
          33: "green",   // Klient - začátek spolupráce
          34: "orange",  // Výměna partnera
          35: "red",     // Klient - konec spolupráce
          36: "yellow",  // Akce na domě
          37: "purple",  // Klient - pozastavení úklidu
          38: "grey",    // Klient - jednorázový úklid
          39: "blue",    // Záskok
        };
        const displayColor = actionColors[record.action_id] || "default";

        return (
          <Tooltip title={displayText}>
            <Tag color={displayColor}>{displayText}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: t("common.notes"),
      key: "notes",
      width: 250,
      fixed: "left",
      render: (text, record) => {
        return (
          <div
            className={"table-cell-editable"}
            onClick={() => showEditModal(record, "notes")}
          >
            <Tooltip title={record.notes} placement="topLeft">
              <span>{record.notes || "-"}</span>
            </Tooltip>
            <EditOutlined className="edit-icon" />
          </div>
        );
      },
    },
    {
      title: t("buildingSetupChanges.actionWeek"),
      key: "action_week_from",
      dataIndex: "action_week_from",
      sorter: true,
      width: 120,
      fixed: "left",
      render: (text, record) => {
        const formatDate = (date) => {
          const d = new Date(date);
          return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}`;
        };

        const fullDate = (date) => {
          const d = new Date(date);
          return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
        };

        const formattedFrom = fullDate(record.action_week_from);
        const formattedTo = record.action_week_to ? fullDate(record.action_week_to) : null;

        const displayText = record.action_week_to
          ? `${formatDate(record.action_week_from)} - ${formatDate(record.action_week_to)}`
          : `${formatDate(record.action_week_from)}`;

        return (
          <span title={record.action_week_to ? `${formattedFrom} - ${formattedTo}` : formattedFrom}>
            {displayText}
          </span>
        );
      },
    },
    {
      title: t("common.partner"),
      key: "partner",
      dataIndex: "partner_first_name",
      sorter: true,
      width: 150,
      fixed: "left",
      filteredValue: filters.partner || null,
      onFilter: (value, record) => record.partner && record.partner.id === value,
      render: (text, record) => {

        return (
          <div
            className="table-cell-editable"
            onClick={() => showEditModal(record, "partner_id")}
          >
            {record?.partner
              ? <PartnerLink partner={record.partner} />
              : "-"
            }
            <EditOutlined className="edit-icon" />
          </div>
        );
      }
      ,
      filterDropdown: ({ confirm }) => (
        <div style={{ padding: 8, width: 280 }}>
          <PartnerSelect
            onChange={(value) => {
              const newFilters = { ...filters, partner: value ? [value.id] : null };
              setFilters(newFilters);
              fetchBuildingUnitSetupChanges(pagination, sorter, newFilters);
              confirm();
            }}
            defaultPartners={defaultPartners}
            allowClear
            style={{ width: 200 }}
          />
        </div>
      ),
    },
    {
      title: t("common.day"),
      key: "day",
      dataIndex: "day",
      width: 120,
      fixed: "left",
      render: (text, record) => {
        const originalDayText = showActiveDays(record.building_unit.cleaning_days, record.building_unit, t, {
          partnerId: record.partner?.id ?? record.partner_id,
          record,
        });

        return (<div
          className="table-cell-editable"
          onClick={() => showEditModal(record, "day")}
        >
          <Tooltip title={originalDayText || record.day}>
            <span>{originalDayText || record.day || "-"}</span>
          </Tooltip>
          <EditOutlined className="edit-icon" />
        </div>);
      },
    },
    {
      title: t("common.substitutePartner"),
      key: "substitute_partner",
      dataIndex: "substitute_partner_first_name",
      sorter: true,
      width: 180,
      filteredValue: filters.substitute_partner || null,
      render: (text, record) => (
        <div className="table-cell-editable" onClick={() => showEditModal(record, "substitute_partner_id")}>
          {record.substitute_partner ? <PartnerLink partner={record.substitute_partner} /> : "-"}
          <EditOutlined className="edit-icon" />
        </div>
      ),
      filterDropdown: ({ confirm }) => (
        <div style={{ padding: 8, width: 280 }}>
          <PartnerSelect
            onChange={(value) => {
              const newFilters = { ...filters, substitute_partner: value ? [value.id] : null };
              setFilters(newFilters);
              fetchBuildingUnitSetupChanges(pagination, sorter, newFilters);
              confirm();
            }}
            defaultPartners={defaultPartners}
            allowClear
            style={{ width: 200 }}
          />
        </div>
      ),
    },
    {
      title: t("common.substituteDay"),
      key: "new_day",
      dataIndex: "new_day",
      width: 140,
      render: (text, record) => {
        const rrule = record.substitute_rrule || null;

        return (
          <div className="table-cell-editable" onClick={() => showEditModal(record, "new_day")}>
            <Tooltip title={formatRruleDays(rrule, t)}>
              <span>{formatRruleDays(rrule, t) || record.new_day || "-"}</span>
            </Tooltip>
            <EditOutlined className="edit-icon" />
          </div>
        );
      },
    },
    {
      title: (
        <span>
          {t("buildingSetupChanges.partnerReward")}{" "}
          <Tooltip title="(activeOrderPrice * 0.6) / buildingUnitsCount">
            <InfoCircleOutlined />
          </Tooltip>
        </span>
      ),
      key: "partner_reward",
      dataIndex: "price_per_unit",
      sorter: true,
      width: 100,
      render: (text, record) => {
        if (record?.price_per_unit) {
          const totalAmount = parseFloat(record.offers_price_total_amount);
          const buildingUnitsCount = record.building_units_count;
          const partnerReward = record?.price_per_unit || 0;

          const res = Number(partnerReward).toFixed(2);
          const tooltipTitle = `(${totalAmount} * 0.6) / ${buildingUnitsCount}`;

          return (
            <span>
              {res}{" "}
              <Tooltip title={tooltipTitle}>
                <InfoCircleOutlined />
              </Tooltip>
            </span>
          );
        } else {
          return "-";
        }
      },
    },
    {
      title: t("common.district"),
      key: "district",
      dataIndex: "building_unit.district",
      width: 150,
      render: (text, record) => {
        return <div>{record.building_unit.district}</div>;
      },
    },
    {
      title: t("buildingSetupChanges.cleaningFrequency"),
      key: "building_unit.service_rrules.sort_weight",
      dataIndex: "cleaningFrequency",
      sorter: false,
      width: 130,
      render: (text, record) => {
        const NameOfFrequency = getTheNameOfFrequency(record);
        const resultText = text || (NameOfFrequency ? t(NameOfFrequency) : null);

        return getTheActiveDays(record, t) ? resultText : '-';
      },
    },
    {
      title: t("buildingSetupChanges.floorCount"),
      key: "floor_count",
      width: 120,
      render: (text, record) => {
        return [record.building_unit.floor_count, record.building_unit.ground_floor_count, record.building_unit.basement_floor_count]
          .filter(value => value !== null && value !== undefined && value !== "")
          .join(", ") || "-";
      },
    },
    {
      title: t("expansion.managerName"),
      key: "manager",
      dataIndex: "manager_first_name",
      sorter: true,
      width: 160,
      filteredValue: filters.manager || [],
      render: (text, record) => (record.manager ? <EmployeeLink employee={record.manager} /> : ""),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div style={{ padding: 8, width: 250 }}>
          <Space direction="vertical">
            <Select
              mode="multiple"
              allowClear
              style={{ width: 220 }}
              placeholder={t("managers.selectManager")}
              value={selectedKeys}
              onChange={(value) => {
                setSelectedKeys(value);
                const newFilters = {
                  ...filters,
                  manager: value.length > 0 ? value : null,
                };
                setFilters(newFilters);
                confirm();
              }}
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
              optionLabelProp="label"
            >
              {managers.map((manager) => (
                <Select.Option
                  key={manager.id}
                  value={manager.id}
                  label={`${manager.first_name} ${manager.last_name}`}
                >
                  {manager.user?.deleted_at === null ? (
                    `${manager.first_name} ${manager.last_name}`
                  ) : (
                    <s>{`${manager.first_name} ${manager.last_name}`}</s>
                  )}
                </Select.Option>
              ))}
            </Select>
          </Space>
        </div>
      ),
    },
    {
      title: t("common.actions"),
      key: "actions",
      width: 100,
      render: (text, record) => (
        <Space size="middle">
          <Popconfirm
            title={t("common.confirmDelete")}
            onConfirm={() => handleDeleteRow(record.id)}
            okText={t("common.confirmAnswer")}
            cancelText={t("common.rejectAnswer")}
          >
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys) => {
      setSelectedRowKeys(selectedRowKeys);
    },
  };

  const showAddModal = () => {
    setIsAddModalVisible(true);
  };

  const hideAddModal = () => {
    resetFormKey("addForm");
    setIsAddModalVisible(false);
  };

  const showDownloadModal = () => {
    setProductType("automatic");
    setDisplayPrice("total");
    setSelectedLanguage("automatic");
    setIsDownloadModalVisible(true);
  };

  const hideDownloadModal = () => {
    resetFormKey("downloadForm");
    setIsDownloadModalVisible(false);
  };

  const showDownloadCleaningPartnerPdfModal = () => {
    setProductType("automatic");
    setDisplayPrice("total");
    setSelectedLanguage("automatic");
    setIsDownloadCleaningPartnerPdfModalVisible(true);
  };

  const hideDownloadCleaningPartnerPdfModal = () => {
    resetFormKey("downloadCleaningPartnerForm");
    setIsDownloadCleaningPartnerPdfModalVisible(false);
  };

  const showMassEditModal = () => {
    const selectedUnits = data.filter((unit) => selectedRowKeys.includes(unit.id));
    if (selectedUnits.length === 0) {
      notification.error({ message: t("buildingSetupChanges.selectRowToEdit") });
      return;
    }
    setIsMassEditModalVisible(true);
  };

  const hideMassEditModal = () => {
    resetFormKey("massEditForm");
    setIsMassEditModalVisible(false);
  };

  const hideEditManyModal = () => {
    resetFormKey("editManyForm");
    setIsEditManyModalVisible(false);
  };

  const resetFormKey = (formName) => {
    setFormKeys((prevKeys) => ({
      ...prevKeys,
      [formName]: Math.random(),
    }));
  };

  const handleApiError = (error) => {
    const isValidationError = !!error?.response?.data?.errors;

    notification.error({
      message: t(isValidationError ? "common.validationError" : "common.error"),
      description: error?.response?.data?.message || error.message,
    });
  };

  const handleShowSendSchedulesForPartnerModal = () => {
    setIsSendSchedulesForPartnerModalVisible(true);
  };

  return (
    <div>
      <Card  style={{ marginBottom: 10 }}>
        <Space style={{ width: "100%" }}>
          <DatePicker.RangePicker
            onChange={(value) => {
              const dateFrom = value ? value[0]?.format("YYYY-MM-DD") : null;
              const dateTo = value ? value[1]?.format("YYYY-MM-DD") : null;

              setFilters({
                ...filters,
                date_from: dateFrom,
                date_to: dateTo,
              });

              fetchBuildingUnitSetupChanges(
                pagination,
                sorter,
                { ...filters, date_from: dateFrom, date_to: dateTo },
              );
            }}
            style={{ marginRight: 5 }}
          />
          <Button
            size="small"
            type="primary"
            onClick={showAddModal}
            icon={<PlusOutlined />}
          >
            {t("buildings.add")}
          </Button>
          <Button
            size="small"
            type="primary"
            icon={<EditOutlined />}
            disabled={selectedRowKeys.length === 0}
            onClick={showMassEditModal}
          >
            {t("common.identicalEdit")}
          </Button>
          <Button
            size="small"
            type="primary"
            icon={<EditOutlined />}
            disabled={selectedRowKeys.length === 0}
            onClick={() => setIsEditManyModalVisible(true)}
          >
            {t("common.differentEdit")}
          </Button>
          <Button
            type="ghost"
            size="small"
            icon={<SaveOutlined />}
            disabled={selectedRowKeys.length === 0}
            onClick={() => handleStatusChange(1)}
            className="btn-status-danger"
          >
            {t("buildingSetupChanges.statusInSolution")}
          </Button>
          <Button
            type="ghost"
            size="small"
            icon={<SaveOutlined />}
            disabled={selectedRowKeys.length === 0}
            onClick={() => handleStatusChange(3)}
            className="btn-status-warning"
          >
            {t("buildingSetupChanges.statusInSolutionPackageConfirmed")}
          </Button>
          <Button
            type="ghost"
            size="small"
            icon={<SaveOutlined />}
            disabled={selectedRowKeys.length === 0}
            onClick={() => handleStatusChange(2)}
            className="btn-status-success"
          >
            {t("buildingSetupChanges.statusSolved")}
          </Button>
          <Button
            size="small"
            type="primary"
            icon={<FilePdfOutlined />}
            disabled={selectedRowKeys.length === 0}
            onClick={showDownloadModal}
          >
            {t("buildingSetupChanges.downloadPdf")}
          </Button>
          <Button
            size="small"
            type="primary"
            icon={<FilePdfOutlined />}
            disabled={selectedRowKeys.length === 0}
            onClick={showDownloadCleaningPartnerPdfModal}
          >
            {t("buildingSetupChanges.downloadCleaningPartnerPdf")}
          </Button>
        </Space>
        <Space style={{ marginTop: 10 }}  key={selectedRowKeys?.join("-")}>
          <Row gutter={[16, 16]}>
            <Col flex="auto">
              <MassInvoiceDuringMonthForm
                key={selectedRowKeys.sort().join("-")}
                selectedBuildingUnits={data
                  .filter((unit) => selectedRowKeys.includes(unit.id))
                  .map((unit) => unit.building_unit.id)}
              />
            </Col>
            <Col>
              <Button
                size="small"
                type="primary"
                icon={<EditOutlined />}
                disabled={selectedRowKeys.length === 0}
                onClick={() => setIsMassCleaningManagementModalVisible(true)}
              >
                {t("cleanings.bulkAction")}
              </Button>
            </Col>
            <Col>
              <Button
                size="small"
                type="default"
                icon={<SendOutlined />}
                onClick={handleShowSendSchedulesForPartnerModal}
              >
                {t("schedules.informPartnerOptions.sendSchedulesForPartner")}
              </Button>
            </Col>
            <Col>
              <SlideDown closed={!selectedRowKeys.length}>
                <ScheduleMassTerminate
                  key={selectedRowKeys.sort().join("-")}
                  selectedBuildingUnits={data
                    .filter(unit => selectedRowKeys.includes(unit.id))
                    .map(unit => ({
                      ...unit.building_unit,
                      partner_id: data.find(d => d.id === selectedRowKeys[0]).partner_id
                    }))}
                  showClientNotify={false}
                />
                {selectedRowKeys && (
                  <div style={{ marginLeft: 16, display: "inline-block"  }}>
                     <span style={{ marginRight: 8 }}>
                      <ClientInformSelect
                        selectedUnits={data.filter((unit) => selectedRowKeys.includes(unit.id))}
                      />
                    </span>

                    <PartnerInformSelect
                      selectedUnits={data.filter((unit) => selectedRowKeys.includes(unit.id))}
                    />
                  </div>
                )}
              </SlideDown>
            </Col>
          </Row>
        </Space>
      </Card>
      <Table
        size="small"
        rowSelection={rowSelection}
        columns={columns}
        dataSource={data}
        loading={loading}
        bordered={true}
        rowKey="id"
        scroll={{ x: 1500, y: 500 }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total, range) => (
            <p>
              {t("common.paginationPartOne")} <strong>{range[0]}</strong>-<strong>{range[1]}</strong>
              {" " + t("common.paginationPartTwo") + " "}
              <strong>{total}</strong> {t("common.paginationPartThree")}
            </p>
          ),
        }}
        sticky
        onChange={handleTableChange}
        rowClassName={(record, index) => record.rowClass}
      />

      <Modal title={t("buildings.add")} open={isAddModalVisible} onCancel={hideAddModal} footer={null}>
        {isAddModalVisible && (
          <AddForm
            key={formKeys.addForm}
            onFormSubmit={() => {
              hideAddModal();
              fetchBuildingUnitSetupChanges(pagination, sorter, filters);
            }}
            onCancel={hideAddModal}
          />
        )}
      </Modal>
      <Modal
        key={formKeys.downloadForm}
        title={t("buildingSetupChanges.downloadPdf")}
        open={isDownloadModalVisible}
        onCancel={hideDownloadModal}
        onOk={handleDownloadPdf}
      >
        <PdfSettingsForm
          key="download-pdf-settings"
          productType={productType}
          setProductType={setProductType}
          rewardVisibility={displayPrice}
          setRewardVisibility={setDisplayPrice}
          selectedLanguage={selectedLanguage}
          setSelectedLanguage={setSelectedLanguage}
          showAutomaticLanguage={true}
          showAutomaticProductType={true}
        />
      </Modal>
      <Modal
        key={formKeys.downloadCleaningPartnerForm}
        title={t("buildingSetupChanges.downloadCleaningPartnerPdf")}
        open={isDownloadCleaningPartnerPdfModalVisible}
        onCancel={hideDownloadCleaningPartnerPdfModal}
        onOk={handleDownloadCleaningPartnerPdfPdf}
      >
        <PdfSettingsForm
          key="download-cleaning-partner-pdf-settings"
          productType={productType}
          setProductType={setProductType}
          rewardVisibility={displayPrice}
          setRewardVisibility={setDisplayPrice}
          selectedLanguage={selectedLanguage}
          setSelectedLanguage={setSelectedLanguage}
          showAutomaticLanguage={true}
          showAutomaticProductType={true}
          customRewardVisibilityOptions={["total", "separate", "none"]}
          hideStreetNumber={hideStreetNumber}
          setHideStreetNumber={setHideStreetNumber}
          showStreetNumberSection={true}
        />
      </Modal>
      <Modal
        title={t("common.identicalEdit")}
        open={isMassEditModalVisible}
        onCancel={hideMassEditModal}
        footer={null}
      >
        {isMassEditModalVisible && (
          <MassEditForm
            key={formKeys.massEditForm}
            selectedUnits={data.filter((unit) => selectedRowKeys.includes(unit.id))}
            onSave={() => {
              hideMassEditModal();
              fetchBuildingUnitSetupChanges(pagination, sorter, filters);
            }}
            onCancel={hideMassEditModal}
          />
        )}
      </Modal>
      <Modal
        width={1200}
        title={t("common.differentEdit")}
        open={isEditManyModalVisible}
        onCancel={hideEditManyModal}
        footer={null}
      >
        {isEditManyModalVisible && (
          <EditManyForm
            key={formKeys.editManyForm}
            onSave={() => {
              hideEditManyModal();
              fetchBuildingUnitSetupChanges(pagination, sorter, filters);
            }}
            items={data.filter((unit) => selectedRowKeys.includes(unit.id))}
            onCancel={hideEditManyModal}
          />
        )}
      </Modal>
      <Modal
        title={t("common.differentEdit")}
        open={isEditModalVisible}
        onCancel={() => {
          setIsEditModalVisible(false);
          setEditingRecord(null);
        }}
        onOk={handleSave}
      >
        {editingRecord && (
          <div>
            <p>
              <strong>{t("common.city")}:</strong> {editingRecord.city_branch.name || "-"}
            </p>
            <p>
              <strong>{t("common.address")}:</strong> <BuildingUnitLink buildingUnit={editingRecord.building_unit} />
            </p>
            {editingField === "notes" && (
              <TextArea
                rows={10}
                maxLength={2000}
                showCount
                placeholder={t("common.notes")}
                value={editingRecord.notes}
                onChange={(e) =>
                  setEditingRecord((prev) => ({
                    ...prev,
                    notes: e.target.value,
                  }))
                }
              />
            )}
            {editingField === "partner_id" && editingRecord && (
              <Form
                layout="vertical"
                key={editingRecord.id}
              >
                <PartnerSelect
                  key={editingField.id}
                  name={"partner_id"}
                  label={t("contacts.filters.partner")}
                  defaultPartners={defaultPartners}
                  initialValue={editingRecord.partner_id}
                  disabledOption={editingRecord.partner_id}
                  allowClear
                  onChange={(value) => {
                    setEditingRecord((prev) => ({
                      ...prev,
                      partner_id: value ? (value?.id || value) : null,
                    }));
                  }
                  }
                />
              </Form>
            )}
            {editingField === "day" && (
              <>
                {renderRruleDaySelect(editingRecord?.rrule || null, editingRecord, "rrule")}
              </>
            )}
            {editingField === "substitute_partner_id" && editingRecord &&  (
              <Form
                layout="vertical"
                key={editingRecord.id}
              >
                <PartnerSelect
                  key={editingField.id}
                  name="substitute_partner_id"
                  label={t("common.substitutePartner")}
                  defaultPartners={defaultPartners}
                  initialValue={editingRecord?.substitute_partner_id}
                  allowClear
                  onChange={(value) => {
                    setEditingRecord((prev) => ({
                      ...prev,
                      substitute_partner_id: value ? (value?.id || value) : null,
                    }));
                  }}
                />
              </Form>
            )}
            {editingField === "new_day" && (
              renderRruleDaySelect(editingRecord?.substitute_rrule || null, editingRecord, "substitute_rrule")
              )}
          </div>)}
      </Modal>
      <Modal
        width={1400}
        open={isMassCleaningManagementModalVisible}
        title={t("cleanings.bulkAction")}
        onCancel={() => setIsMassCleaningManagementModalVisible(false)}
        footer={null}
      >
        <MassBuildingUnitCleaningManagement
          partners={Array.from(
            new Map(
              data
                .filter((unit) => selectedRowKeys.includes(unit.id))
                .map((unit) => unit.partner)
                .filter(Boolean)
                .map((p) => [p.id, p])
            ).values()
          )}
          buildingUnits={data
            .filter((unit) => selectedRowKeys.includes(unit.id))
            .map((unit) => unit.building_unit)}
        />
      </Modal>
      
      {/* Send Cleaning Schedules for Partner Modal */}
      <SendCleaningSchedulesForPartnerModal
        isVisible={isSendSchedulesForPartnerModalVisible}
        onCancel={() => setIsSendSchedulesForPartnerModalVisible(false)}
        handleApiError={handleApiError}
      />
    </div>
  );
};

export default BuildingUnitSetupChangesIndex;
